export interface MemoryItem {
  id: string
  content: string
  type: 'conversation' | 'fact' | 'preference' | 'context'
  timestamp: number
  importance: number
  source?: string
  metadata?: Record<string, any>
}

export interface ConversationHistory {
  id: string
  messages: Array<{
    role: 'user' | 'assistant'
    content: string
    timestamp: number
  }>
  lastUpdated: number
  topic?: string
}

export interface UserPreferences {
  language: string
  tone: 'friendly' | 'professional' | 'concise'
  detailLevel: 'simple' | 'detailed' | 'comprehensive'
  responseStyle: 'bullet' | 'paragraph' | 'structured'
  custom: Record<string, any>
}

export class AIMemoryManager {
  private memories: Map<string, MemoryItem> = new Map()
  private conversations: Map<string, ConversationHistory> = new Map()
  private userPreferences: UserPreferences
  private maxMemories: number = 1000
  private maxConversationHistory: number = 50
  private currentConversationId: string

  constructor() {
    this.currentConversationId = this.generateSessionId()
    this.userPreferences = {
      language: 'zh-CN',
      tone: 'friendly',
      detailLevel: 'detailed',
      responseStyle: 'structured',
      custom: {}
    }
    this.loadFromStorage()
  }

  private generateSessionId(): string {
    return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  addMemory(content: string, type: MemoryItem['type'], options: {
    importance?: number
    source?: string
    metadata?: Record<string, any>
  } = {}): string {
    const id = `memory-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const memory: MemoryItem = {
      id,
      content,
      type,
      timestamp: Date.now(),
      importance: options.importance || 1,
      source: options.source,
      metadata: options.metadata
    }

    this.memories.set(id, memory)
    this.trimMemories()
    this.saveToStorage()
    return id
  }

  getMemories(options: {
    type?: MemoryItem['type']
    limit?: number
    minImportance?: number
    since?: number
  } = {}): MemoryItem[] {
    let memories = Array.from(this.memories.values())

    if (options.type) {
      memories = memories.filter(m => m.type === options.type)
    }

    if (options.minImportance !== undefined) {
      memories = memories.filter(m => m.importance >= options.minImportance)
    }

    if (options.since) {
      memories = memories.filter(m => m.timestamp >= options.since)
    }

    memories.sort((a, b) => {
      const importanceDiff = b.importance - a.importance
      if (importanceDiff !== 0) return importanceDiff
      return b.timestamp - a.timestamp
    })

    if (options.limit) {
      memories = memories.slice(0, options.limit)
    }

    return memories
  }

  searchMemories(query: string, limit: number = 10): MemoryItem[] {
    const memories = this.getMemories({ limit: 100 })
    const lowerQuery = query.toLowerCase()

    const scoredMemories = memories.map(memory => ({
      memory,
      score: this.calculateRelevance(memory, lowerQuery)
    })).filter(item => item.score > 0)

    scoredMemories.sort((a, b) => b.score - a.score)

    return scoredMemories.slice(0, limit).map(item => item.memory)
  }

  private calculateRelevance(memory: MemoryItem, query: string): number {
    const content = memory.content.toLowerCase()
    let score = 0

    if (content.includes(query)) {
      score += 10
    }

    const queryWords = query.split(/\s+/)
    const matchingWords = queryWords.filter(word => content.includes(word)).length
    score += matchingWords * 2

    score += memory.importance * 0.5

    const ageHours = (Date.now() - memory.timestamp) / (1000 * 60 * 60)
    if (ageHours < 1) {
      score += 5
    } else if (ageHours < 24) {
      score += 3
    }

    return score
  }

  addConversationMessage(role: 'user' | 'assistant', content: string, conversationId?: string): void {
    const id = conversationId || this.currentConversationId
    let conversation = this.conversations.get(id)

    if (!conversation) {
      conversation = {
        id,
        messages: [],
        lastUpdated: Date.now()
      }
    }

    conversation.messages.push({
      role,
      content,
      timestamp: Date.now()
    })

    if (conversation.messages.length > this.maxConversationHistory) {
      conversation.messages = conversation.messages.slice(-this.maxConversationHistory)
    }

    conversation.lastUpdated = Date.now()
    this.conversations.set(id, conversation)
    
    if (role === 'user') {
      this.addMemory(content, 'conversation', { importance: 1, source: 'user' })
    }

    this.saveToStorage()
  }

  getConversationHistory(conversationId?: string, limit?: number): ConversationHistory | undefined {
    const id = conversationId || this.currentConversationId
    let conversation = this.conversations.get(id)

    if (!conversation) {
      return undefined
    }

    if (limit && conversation.messages.length > limit) {
      return {
        ...conversation,
        messages: conversation.messages.slice(-limit)
      }
    }

    return conversation
  }

  getRecentMessages(count: number, conversationId?: string): Array<{
    role: 'user' | 'assistant'
    content: string
  }> {
    const conversation = this.getConversationHistory(conversationId)
    if (!conversation) return []
    return conversation.messages.slice(-count)
  }

  setUserPreference<K extends keyof UserPreferences>(key: K, value: UserPreferences[K]): void {
    this.userPreferences[key] = value
    this.addMemory(
      `用户偏好设置：${String(key)} = ${JSON.stringify(value)}`,
      'preference',
      { importance: 2, source: 'user_setting' }
    )
    this.saveToStorage()
  }

  getUserPreferences(): UserPreferences {
    return { ...this.userPreferences }
  }

  setConversationTopic(topic: string, conversationId?: string): void {
    const id = conversationId || this.currentConversationId
    const conversation = this.conversations.get(id)
    if (conversation) {
      conversation.topic = topic
      this.conversations.set(id, conversation)
      this.saveToStorage()
    }
  }

  getContextForPrompt(query: string, options: {
    includeMemories?: number
    includeConversation?: number
  } = {}): string {
    const contextParts: string[] = []

    if (options.includeMemories && options.includeMemories > 0) {
      const relevantMemories = this.searchMemories(query, options.includeMemories)
      if (relevantMemories.length > 0) {
        contextParts.push('相关记忆：')
        relevantMemories.forEach(m => {
          contextParts.push(`- ${m.content}`)
        })
      }
    }

    if (options.includeConversation && options.includeConversation > 0) {
      const recentMessages = this.getRecentMessages(options.includeConversation)
      if (recentMessages.length > 0) {
        contextParts.push('\n最近对话：')
        recentMessages.forEach(msg => {
          contextParts.push(`${msg.role === 'user' ? '用户' : '助手'}: ${msg.content}`)
        })
      }
    }

    return contextParts.join('\n')
  }

  clearMemory(id: string): void {
    this.memories.delete(id)
    this.saveToStorage()
  }

  clearAllMemories(): void {
    this.memories.clear()
    this.saveToStorage()
  }

  clearConversation(conversationId?: string): void {
    const id = conversationId || this.currentConversationId
    this.conversations.delete(id)
    if (id === this.currentConversationId) {
      this.currentConversationId = this.generateSessionId()
    }
    this.saveToStorage()
  }

  switchConversation(conversationId: string): void {
    this.currentConversationId = conversationId
  }

  getAllConversations(): Array<{
    id: string
    topic?: string
    lastUpdated: number
    messageCount: number
  }> {
    return Array.from(this.conversations.values())
      .sort((a, b) => b.lastUpdated - a.lastUpdated)
      .map(c => ({
        id: c.id,
        topic: c.topic,
        lastUpdated: c.lastUpdated,
        messageCount: c.messages.length
      }))
  }

  private trimMemories(): void {
    if (this.memories.size <= this.maxMemories) return

    const sortedMemories = Array.from(this.memories.entries())
      .sort((a, b) => {
        const importanceDiff = b[1].importance - a[1].importance
        if (importanceDiff !== 0) return importanceDiff
        return b[1].timestamp - a[1].timestamp
      })

    const toRemove = sortedMemories.slice(this.maxMemories)
    toRemove.forEach(([id]) => {
      this.memories.delete(id)
    })
  }

  private saveToStorage(): void {
    try {
      const data = {
        memories: Array.from(this.memories.values()),
        conversations: Array.from(this.conversations.values()),
        preferences: this.userPreferences,
        currentConversationId: this.currentConversationId
      }
      localStorage.setItem('ai-memory', JSON.stringify(data))
    } catch (e) {
      console.warn('Failed to save AI memory:', e)
    }
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem('ai-memory')
      if (!stored) return

      const data = JSON.parse(stored)
      
      if (data.memories) {
        data.memories.forEach((m: MemoryItem) => {
          this.memories.set(m.id, m)
        })
      }

      if (data.conversations) {
        data.conversations.forEach((c: ConversationHistory) => {
          this.conversations.set(c.id, c)
        })
      }

      if (data.preferences) {
        this.userPreferences = data.preferences
      }

      if (data.currentConversationId) {
        this.currentConversationId = data.currentConversationId
      }
    } catch (e) {
      console.warn('Failed to load AI memory:', e)
    }
  }

  exportMemory(): string {
    const data = {
      exportedAt: new Date().toISOString(),
      memories: Array.from(this.memories.values()),
      conversations: Array.from(this.conversations.values()),
      preferences: this.userPreferences
    }
    return JSON.stringify(data, null, 2)
  }

  getStats() {
    return {
      memoryCount: this.memories.size,
      conversationCount: this.conversations.size,
      totalMessages: Array.from(this.conversations.values())
        .reduce((sum, c) => sum + c.messages.length, 0),
      oldestMemory: this.getMemories({ limit: 1 }).pop()?.timestamp,
      newestMemory: this.getMemories({ limit: 1, minImportance: 0 }).shift()?.timestamp
    }
  }
}

export interface ScoredMemory {
  memory: MemoryItem
  score: number
  layer: 'short_term' | 'long_term'
}

interface UserMemoryVault {
  userId: string
  shortTerm: Map<string, MemoryItem>
  longTerm: Map<string, MemoryItem>
  shortTermMaxAge: number
  longTermMaxItems: number
  vectorVocab: Set<string>
}

const SHORT_TERM_MAX_AGE_MS = 7 * 24 * 3600 * 1000
const LONG_TERM_MAX_ITEMS = 500
const STORAGE_KEY_VAULT_PREFIX = 'ai_memory_vault_'
const STORAGE_KEY_VOCAB_PREFIX = 'ai_vocab_'

class MultiUserAIMemoryManager {
  private vaults: Map<string, UserMemoryVault> = new Map()

  private _getOrCreateVault(userId: string): UserMemoryVault {
    let v = this.vaults.get(userId)
    if (!v) {
      v = {
        userId,
        shortTerm: new Map(),
        longTerm: new Map(),
        shortTermMaxAge: SHORT_TERM_MAX_AGE_MS,
        longTermMaxItems: LONG_TERM_MAX_ITEMS,
        vectorVocab: new Set()
      }
      this.vaults.set(userId, v)
      this._loadVault(v)
    }
    return v
  }

  private _vaultStorageKey(userId: string) {
    return STORAGE_KEY_VAULT_PREFIX + userId
  }

  private _vocabStorageKey(userId: string) {
    return STORAGE_KEY_VOCAB_PREFIX + userId
  }

  private _loadVault(vault: UserMemoryVault): void {
    try {
      const raw = localStorage.getItem(this._vaultStorageKey(vault.userId))
      if (raw) {
        const data = JSON.parse(raw)
        if (data.shortTerm) {
          data.shortTerm.forEach((m: MemoryItem) => vault.shortTerm.set(m.id, m))
        }
        if (data.longTerm) {
          data.longTerm.forEach((m: MemoryItem) => vault.longTerm.set(m.id, m))
        }
      }
      const vocabRaw = localStorage.getItem(this._vocabStorageKey(vault.userId))
      if (vocabRaw) {
        const arr: string[] = JSON.parse(vocabRaw)
        arr.forEach(w => vault.vectorVocab.add(w))
      }
    } catch (e) {
      console.warn('加载用户记忆失败:', vault.userId, e)
    }
    this._expireShortTerm(vault)
  }

  private _persistVault(vault: UserMemoryVault): void {
    try {
      const payload = {
        shortTerm: Array.from(vault.shortTerm.values()),
        longTerm: Array.from(vault.longTerm.values())
      }
      localStorage.setItem(this._vaultStorageKey(vault.userId), JSON.stringify(payload))
      localStorage.setItem(
        this._vocabStorageKey(vault.userId),
        JSON.stringify(Array.from(vault.vectorVocab))
      )
    } catch (e) {
      console.warn('保存用户记忆失败:', vault.userId, e)
    }
  }

  private _expireShortTerm(vault: UserMemoryVault): void {
    const now = Date.now()
    const promoteCandidates: MemoryItem[] = []
    Array.from(vault.shortTerm.entries()).forEach(([id, m]) => {
      const age = now - m.timestamp
      if (age > vault.shortTermMaxAge) {
        vault.shortTerm.delete(id)
        if ((m.importance ?? 1) >= 3) {
          promoteCandidates.push(m)
        }
      }
    })
    promoteCandidates.forEach(m => this._tryPromote(vault, m))
  }

  private _tryPromote(vault: UserMemoryVault, memory: MemoryItem): void {
    if (vault.longTerm.size >= vault.longTermMaxItems) {
      const sorted = Array.from(vault.longTerm.values()).sort((a, b) => {
        const r = (a.importance ?? 1) - (b.importance ?? 1)
        if (r !== 0) return r
        return a.timestamp - b.timestamp
      })
      const remove = sorted[0]
      if (remove && (memory.importance ?? 1) > (remove.importance ?? 1)) {
        vault.longTerm.delete(remove.id)
      } else {
        return
      }
    }
    vault.longTerm.set(memory.id, { ...memory, source: memory.source || 'promoted_from_short' })
  }

  private _registerVocab(vault: UserMemoryVault, text: string): void {
    const tokens = this._tokenize(text)
    tokens.forEach(t => vault.vectorVocab.add(t))
  }

  private _tokenize(text: string): string[] {
    if (!text) return []
    const cleaned = text.toLowerCase()
      .replace(/[\p{P}\p{S}\s\n\r\t]+/gu, ' ')
      .trim()
    const tokens: string[] = []
    const ascii = cleaned.match(/[a-z0-9_]{2,}/g) || []
    tokens.push(...ascii)
    const chinese = cleaned.replace(/[a-z0-9_\s]/g, '')
    for (let i = 0; i < chinese.length - 1; i++) {
      tokens.push(chinese.slice(i, i + 2))
    }
    for (let i = 0; i < chinese.length; i++) {
      tokens.push(chinese[i])
    }
    return Array.from(new Set(tokens))
  }

  private _buildVector(vault: UserMemoryVault, text: string): Map<string, number> {
    const vec = new Map<string, number>()
    const tokens = this._tokenize(text)
    const tf = new Map<string, number>()
    tokens.forEach(t => {
      tf.set(t, (tf.get(t) || 0) + 1)
      vault.vectorVocab.add(t)
    })
    const maxFreq = Math.max(1, ...Array.from(tf.values()))
    vault.vectorVocab.forEach(word => {
      const raw = tf.get(word) || 0
      const normalized = raw > 0 ? 0.5 + 0.5 * (raw / maxFreq) : 0
      if (normalized > 0) vec.set(word, normalized)
    })
    return vec
  }

  private _cosineSimilarity(a: Map<string, number>, b: Map<string, number>): number {
    if (a.size === 0 || b.size === 0) return 0
    let dot = 0
    let normA = 0
    let normB = 0
    const keys = new Set([...a.keys(), ...b.keys()])
    keys.forEach(k => {
      const va = a.get(k) || 0
      const vb = b.get(k) || 0
      dot += va * vb
      normA += va * va
      normB += vb * vb
    })
    const denom = Math.sqrt(normA) * Math.sqrt(normB)
    return denom > 0 ? dot / denom : 0
  }

  addMemory(
    userId: string,
    memoryText: string,
    tags: string[] = [],
    importance: number = 1,
    options: { type?: MemoryItem['type']; source?: string; metadata?: Record<string, any> } = {}
  ): string {
    const vault = this._getOrCreateVault(userId)
    const id = `um_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
    const memory: MemoryItem = {
      id,
      content: memoryText,
      type: options.type || 'fact',
      timestamp: Date.now(),
      importance: Math.max(1, Math.min(5, importance)),
      source: options.source || 'user',
      metadata: { ...(options.metadata || {}), tags }
    }
    vault.shortTerm.set(id, memory)
    this._registerVocab(vault, memoryText + ' ' + tags.join(' '))
    if (memory.importance >= 4) {
      this._tryPromote(vault, memory)
    }
    this._persistVault(vault)
    return id
  }

  searchMemory(
    userId: string,
    query: string,
    topK: number = 10,
    options: { layers?: ('short_term' | 'long_term')[]; minImportance?: number } = {}
  ): ScoredMemory[] {
    const vault = this._getOrCreateVault(userId)
    this._expireShortTerm(vault)
    const layers = options.layers || ['short_term', 'long_term']
    const qVec = this._buildVector(vault, query)
    const candidates: ScoredMemory[] = []

    if (layers.includes('short_term')) {
      Array.from(vault.shortTerm.values()).forEach(m => {
        if (options.minImportance !== undefined && (m.importance ?? 1) < options.minImportance) return
        const mVec = this._buildVector(vault, m.content + ' ' + ((m.metadata?.tags as string[]) || []).join(' '))
        const cos = this._cosineSimilarity(qVec, mVec)
        const ageHours = (Date.now() - m.timestamp) / 3600000
        const ageBoost = Math.exp(-ageHours / 72)
        const score = cos * 0.7 + ((m.importance ?? 1) / 5) * 0.2 + ageBoost * 0.1
        if (score > 0) candidates.push({ memory: m, score, layer: 'short_term' })
      })
    }

    if (layers.includes('long_term')) {
      Array.from(vault.longTerm.values()).forEach(m => {
        if (options.minImportance !== undefined && (m.importance ?? 1) < options.minImportance) return
        const mVec = this._buildVector(vault, m.content + ' ' + ((m.metadata?.tags as string[]) || []).join(' '))
        const cos = this._cosineSimilarity(qVec, mVec)
        const ageDays = (Date.now() - m.timestamp) / 86400000
        const ageBoost = Math.exp(-ageDays / 90)
        const score = cos * 0.75 + ((m.importance ?? 1) / 5) * 0.22 + ageBoost * 0.03
        if (score > 0) candidates.push({ memory: m, score, layer: 'long_term' })
      })
    }

    candidates.sort((a, b) => b.score - a.score)
    this._persistVault(vault)
    return candidates.slice(0, topK)
  }

  getUserMemories(
    userId: string,
    options: { layer?: 'short_term' | 'long_term'; type?: MemoryItem['type']; limit?: number; minImportance?: number } = {}
  ): MemoryItem[] {
    const vault = this._getOrCreateVault(userId)
    this._expireShortTerm(vault)
    const source = options.layer === 'long_term' ? vault.longTerm : options.layer === 'short_term' ? vault.shortTerm : new Map([...vault.shortTerm, ...vault.longTerm])
    let arr = Array.from(source.values())
    if (options.type) arr = arr.filter(m => m.type === options.type)
    if (options.minImportance !== undefined) arr = arr.filter(m => (m.importance ?? 1) >= options.minImportance)
    arr.sort((a, b) => {
      const d = (b.importance ?? 1) - (a.importance ?? 1)
      return d !== 0 ? d : b.timestamp - a.timestamp
    })
    return options.limit ? arr.slice(0, options.limit) : arr
  }

  clearUserMemory(userId: string, layer?: 'short_term' | 'long_term'): void {
    const vault = this._getOrCreateVault(userId)
    if (!layer || layer === 'short_term') vault.shortTerm.clear()
    if (!layer || layer === 'long_term') vault.longTerm.clear()
    if (!layer) vault.vectorVocab.clear()
    this._persistVault(vault)
  }

  getUserMemoryStats(userId: string) {
    const vault = this._getOrCreateVault(userId)
    this._expireShortTerm(vault)
    return {
      userId,
      shortTermCount: vault.shortTerm.size,
      longTermCount: vault.longTerm.size,
      vocabSize: vault.vectorVocab.size,
      totalImportanceWeight:
        Array.from(vault.shortTerm.values()).reduce((s, m) => s + (m.importance ?? 1), 0) +
        Array.from(vault.longTerm.values()).reduce((s, m) => s + (m.importance ?? 1), 0)
    }
  }

  promoteToLongTerm(userId: string, memoryId: string): boolean {
    const vault = this._getOrCreateVault(userId)
    const m = vault.shortTerm.get(memoryId) || vault.longTerm.get(memoryId)
    if (!m) return false
    this._tryPromote(vault, m)
    this._persistVault(vault)
    return vault.longTerm.has(memoryId)
  }
}

export const aiMemoryManager = new AIMemoryManager()
export const multiUserAIMemoryManager = new MultiUserAIMemoryManager()
export default multiUserAIMemoryManager
